import { isNotNil } from '@flowio/is';
import fetchAuthorization from '../services/authorization/fetchAuthorization';
import { Nullable } from '../types';
import getRollbarInstance from './getRollbarInstance';

const DEFAULT_START_INTERVAL = 3;
const DEFAULT_TIMEOUT_INTERVAL = 30;

/**
 * @description Polls a PR for changes on the server determined by the `options.diffCheck` function
 * passed.
 */
export default function pollForAuthorizationUpdate(
  params: {
    /**
     * Authorization Result Action to check for changes.
     */
    auth: io.flow.v0.unions.Authorization,
    organizationId: string,
    sessionId: string,
    /**
     * @description tests one PR against another to determine if it has changed.
     */
    diffCheck: (
      oldAuth: io.flow.v0.unions.Authorization,
      newAuth: io.flow.v0.unions.Authorization,
    ) => boolean,
    /**
     * @description Callback when PR has updated according to diffCheck function.
     */
    onChange: (auth: io.flow.v0.unions.Authorization) => void,
    /**
     * @description Callback for when polling times out.
     */
    onTimeout?: () => void,
    /**
     * @description The length of time in seconds before the first poll should occur
     * @default 3
     */
    startInterval?: number,
    /**
     * @description The length of time in seconds when to timeout polling after beginning.
     * @default 30
     */
    timeoutInterval?: number,
  },
): () => void {
  return (function pollForAutorizationUpdateEffect(): () => void {
    let timerId: Nullable<ReturnType<typeof setTimeout>>;
    let lastInterval = (params.startInterval
      ? params.startInterval : DEFAULT_START_INTERVAL) * 1000;

    const timeout = (
      params.timeoutInterval ? params.timeoutInterval : DEFAULT_TIMEOUT_INTERVAL
    ) * 1000;

    const timeoutTime = Date.now() + timeout;

    function poller(): void {
      if (Date.now() >= timeoutTime) {
        if (params.onTimeout) {
          params.onTimeout();
        }
        return;
      }

      // debugger;

      fetchAuthorization({
        key: params.auth.key,
        organizationId: params.organizationId,
        sessionId: params.sessionId,
      }).then((updatedAuth) => {
        if (isNotNil(updatedAuth) && params.diffCheck(params.auth, updatedAuth)) {
          params.onChange(updatedAuth);
          return updatedAuth;
        }
        // if the timer has not been cleared, safe to poll again.
        if (timerId !== null) {
          // backoff polling exponentially
          lastInterval *= 1.5;
          timerId = setTimeout(poller, Math.round(lastInterval));
        }
        return undefined;
      }).catch((error) => {
        const message = error instanceof Error ? error.message : 'Unkown error';
        getRollbarInstance().warn(`pollForPrUpdate: ${message}`);
      });
    }

    timerId = setTimeout(() => poller(), lastInterval);

    return (): void => {
      if (timerId !== null) {
        window.clearTimeout(timerId);
        timerId = null;
      }
    };
  }());
}
